import "react-data-grid/lib/styles.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import { CAlert, CButton } from "@coreui/react";
import { Document, Page, pdfjs } from "react-pdf";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { memo, useRef, useState } from "react";

import { Loading } from "src/reusable/LoadingSpinner";
import PDFDownloadButton from "./PDFDownloadButton";
import PropTypes from "prop-types";

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

const PDFViewer = ({ file, windowHeight, pageNum, showDownload }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(pageNum);
  const [leftHighlight, setLeftHighlight] = useState(false);
  const [rightHighlight, setRightHighlight] = useState(false);
  const isSelectingText = useRef(false);

  function onDocumentLoadSuccess({ numPages: pageAmount }) {
    setNumPages(pageAmount);
  }

  function changePage(offset) {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  }

  function previousPage() {
    if (pageNumber > 1) {
      changePage(-1);
      setLeftHighlight(true);
      setTimeout(() => setLeftHighlight(false), 200); // Remove highlight after 200ms
    }
  }

  function nextPage() {
    if (pageNumber < numPages) {
      changePage(1);
      setRightHighlight(true);
      setTimeout(() => setRightHighlight(false), 200); // Remove highlight after 200ms
    }
  }

  const handlePageClick = (e) => {
    if (isSelectingText.current) {
      // Do not change page if text selection is happening
      return;
    }
    const { left, right } = e.target.getBoundingClientRect(); // Provides the left and right boundaries of pdfviewer element
    const clickX = e.clientX; // clientX is the x-coordinate of where the mouse click occured

    if (clickX < left + (right - left) / 2) {
      previousPage();
    } else {
      nextPage();
    }
  };

  const handleMouseDown = () => {
    isSelectingText.current = false;
  };

  const handleMouseMove = () => {
    if (window.getSelection().toString()) {
      isSelectingText.current = true;
    }
  };

  const handleMouseUp = () => {
    setTimeout(() => {
      isSelectingText.current = false;
    }, 100);
  };

  const handlePreviousPageClick = (e) => {
    e.stopPropagation(); // Prevents triggering the page click handler
    previousPage();
  };

  const handleNextPageClick = (e) => {
    e.stopPropagation(); // Prevents triggering the page click handler
    nextPage();
  };

  return (
    <>
      {showDownload && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
            padding: "0 10px",
          }}
        >
          <PDFDownloadButton
            file={file}
            fileName={`document-${Date.now()}.pdf`}
          />
        </div>
      )}
      <div
        onClick={handlePageClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{
          position: "relative",
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <CButton
          onClick={handlePreviousPageClick}
          disabled={pageNumber <= 1}
          data-attr="reports:btn:pdf-previous-page"
          style={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            backgroundColor: leftHighlight ? "lightblue" : "",
          }}
        >
          <FaArrowLeft />
        </CButton>
        <div>
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            error={<CAlert color="danger">Error loading the PDF</CAlert>}
            loading={<Loading width={200} />}
          >
            <MemoizedPage pageNumber={pageNumber} windowHeight={windowHeight} />
          </Document>
          <div style={{ textAlign: "center" }}>
            Page {pageNumber} of {numPages}
          </div>
        </div>
        <CButton
          onClick={handleNextPageClick}
          disabled={pageNumber >= numPages}
          data-attr="reports:btn:pdf-next-page"
          style={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            backgroundColor: rightHighlight ? "lightblue" : "",
          }}
        >
          <FaArrowRight />
        </CButton>
      </div>
    </>
  );
};

PDFViewer.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  windowHeight: PropTypes.number.isRequired,
  pageNum: PropTypes.number,
  // Off when the host already offers a download that knows the file's name.
  showDownload: PropTypes.bool,
};

PDFViewer.defaultProps = {
  pageNum: 1,
  showDownload: true,
};

const MemoizedPage = memo(({ pageNumber, windowHeight }) => (
  <Page pageNumber={pageNumber} height={windowHeight} />
));

MemoizedPage.propTypes = {
  pageNumber: PropTypes.number,
  windowHeight: PropTypes.number,
};
MemoizedPage.displayName = "MemoizedPage";

export default PDFViewer;
